import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, Heart, ExternalLink } from "lucide-react";
import { LogoMark } from "@/components/site/Logo";
import { CONTACT } from "@/data/content";

const CARF_LOGO =
  "https://customer-assets-0z36b82j.emergentagent.net/job_fresh-start-journey/artifacts/ksxkelb3_image.png";

const SOCIALS = [
  {
    name: "Instagram",
    href: "https://instagram.com/newbeginningsrecoverygta",
    icon: "https://cdn.simpleicons.org/instagram/E4405F",
    ring: "#E4405F",
  },
  {
    name: "TikTok",
    href: "https://tiktok.com/@new.beginnings.gta",
    icon: "https://cdn.simpleicons.org/tiktok/000000",
    ring: "#000000",
  },
  {
    name: "LinkedIn",
    href: "https://www.linkedin.com/company/130213899",
    // Inline LinkedIn mark (they removed themselves from Simple Icons CDN)
    inline: (
      <svg viewBox="0 0 24 24" fill="#0A66C2" aria-hidden className="w-full h-full">
        <path d="M20.45 20.45h-3.55v-5.57c0-1.33-.03-3.04-1.85-3.04-1.86 0-2.14 1.45-2.14 2.95v5.66H9.36V9h3.41v1.56h.05a3.74 3.74 0 0 1 3.37-1.85c3.6 0 4.27 2.37 4.27 5.46v6.28zM5.34 7.43a2.06 2.06 0 1 1 0-4.12 2.06 2.06 0 0 1 0 4.12zM7.12 20.45H3.56V9h3.56v11.45zM22.22 0H1.77C.79 0 0 .78 0 1.73v20.53C0 23.22.79 24 1.77 24h20.45c.98 0 1.78-.78 1.78-1.74V1.73C24 .78 23.2 0 22.22 0z" />
      </svg>
    ),
    ring: "#0A66C2",
  },
  {
    name: "Facebook",
    href: "https://www.facebook.com/share/1M15twsb3E/",
    icon: "https://cdn.simpleicons.org/facebook/1877F2",
    ring: "#1877F2",
  },
];

const Footer = () => {
  return (
    <footer data-testid="site-footer" className="bg-forest text-sand">
      <div className="mx-auto max-w-[1400px] px-5 md:px-10 py-16 md:py-24">
        <div className="grid md:grid-cols-12 gap-12">
          {/* Brand + crisis */}
          <div className="md:col-span-5">
            <div className="flex items-center gap-2.5">
              <span className="w-10 h-10 rounded-full overflow-hidden shrink-0">
                <LogoMark className="w-full h-full" />
              </span>
              <span className="font-serif text-2xl">New Beginnings Recovery</span>
            </div>
            <p className="mt-6 text-sand/75 max-w-md leading-relaxed">
              A licensed inpatient home for drug &amp; alcohol addiction and mental-health care near
              the Scarborough Bluffs, Toronto. Compassionate, evidence-based residential rehab —
              a new path to a new life.
            </p>

            {/* Social icons — real brand logos */}
            <div className="mt-6 flex items-center gap-2.5" data-testid="footer-socials">
              {SOCIALS.map(({ name, href, icon, inline, ring }) => (
                <a
                  key={name}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={name}
                  data-testid={`social-${name.toLowerCase()}`}
                  style={{ boxShadow: `0 0 0 0 ${ring}` }}
                  className="grid place-items-center w-11 h-11 rounded-full bg-white border border-sand/10 hover:-translate-y-0.5 hover:shadow-[0_10px_25px_-10px_rgba(0,0,0,0.4)] transition-all p-2.5"
                >
                  {inline ? inline : (
                    <img
                      src={icon}
                      alt={`${name} logo`}
                      className="w-full h-full object-contain"
                      loading="lazy"
                      draggable={false}
                    />
                  )}
                </a>
              ))}
            </div>

            {/* Support / Sponsor a Bed */}
            <a
              href="https://www.zeffy.com/en-CA/donation-form/28-days-to-recovery-sponsor-a-bed-change-a-life"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-sponsor-bed"
              className="mt-6 inline-flex items-center gap-2 rounded-full bg-terracotta hover:bg-[#a8664b] text-white px-5 py-3 text-sm font-semibold transition-colors"
            >
              <Heart className="w-4 h-4" /> Sponsor a bed, save a life
              <ExternalLink className="w-3.5 h-3.5 opacity-70" />
            </a>

            <div className="mt-8 rounded-2xl bg-forestdark/60 border border-sand/10 p-5">
              <p className="text-xs uppercase tracking-[0.2em] text-sage">In crisis right now?</p>
              <p className="mt-2 text-sm text-sand/80">
                If you or someone you love is in immediate danger, call 911. Talk Suicide Canada: 1-833-456-4566.
              </p>
            </div>
          </div>

          {/* Explore */}
          <div className="md:col-span-3">
            <h4 className="font-serif text-xl mb-5">Explore</h4>
            <ul className="space-y-3 text-sand/75">
              <li><Link to="/" className="hover:text-terracotta transition-colors">Home</Link></li>
              <li><Link to="/greenshield" className="hover:text-terracotta transition-colors">GreenShield Coverage</Link></li>
              <li><Link to="/other-insurance" className="hover:text-terracotta transition-colors">Other Insurance</Link></li>
              <li><Link to="/verify-benefits" className="hover:text-terracotta transition-colors">Verify My Benefits</Link></li>
              <li><Link to="/programs/inpatient-treatment" className="hover:text-terracotta transition-colors">Inpatient Program</Link></li>
              <li><Link to="/programs/aftercare" className="hover:text-terracotta transition-colors">Aftercare</Link></li>
              <li><Link to="/resources" className="hover:text-terracotta transition-colors">Resources</Link></li>
              <li><Link to="/faqs" className="hover:text-terracotta transition-colors">FAQ</Link></li>
              <li><Link to="/contact" className="hover:text-terracotta transition-colors">Contact</Link></li>
            </ul>
          </div>

          {/* Reach us + CARF */}
          <div className="md:col-span-4">
            <h4 className="font-serif text-xl mb-5">Reach Us</h4>
            <ul className="space-y-4 text-sand/85">
              <li>
                <a href={CONTACT.phoneHref} className="flex items-center gap-3 hover:text-terracotta transition-colors" data-testid="footer-phone">
                  <Phone className="w-5 h-5 text-terracotta" /> {CONTACT.phone}
                </a>
              </li>
              <li>
                <a href={`mailto:${CONTACT.email}`} className="flex items-center gap-3 hover:text-terracotta transition-colors" data-testid="footer-email">
                  <Mail className="w-5 h-5 text-terracotta" /> {CONTACT.email}
                </a>
              </li>
              <li className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-terracotta mt-1 shrink-0" />
                <span>
                  {CONTACT.address_line1}<br />
                  {CONTACT.address_line2}<br />
                  <span className="text-sand/60 text-sm">{CONTACT.address_note}</span>
                </span>
              </li>
            </ul>

            {/* CARF & LegitScript accreditations */}
            <div className="mt-8 flex items-center gap-3 flex-wrap" data-testid="accreditations">
              <div className="flex items-center gap-3 rounded-2xl bg-white p-3 pr-5" data-testid="carf-badge">
                <img
                  src={CARF_LOGO}
                  alt="CARF International"
                  className="w-14 h-14 object-contain shrink-0"
                  loading="lazy"
                  draggable={false}
                />
                <p className="font-serif text-forest text-base leading-tight font-bold">CARF<br /><span className="text-ink2 text-[11px] font-medium tracking-normal">International</span></p>
              </div>
              <a
                href="https://www.legitscript.com/websites/?checker_keywords=newbeginningsrecovery.ca"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 rounded-2xl bg-white p-3 pr-5 hover:shadow-[0_10px_25px_-10px_rgba(0,0,0,0.4)] transition-shadow"
                data-testid="legitscript-badge"
              >
                <img
                  src="https://customer-assets-0z36b82j.emergentagent.net/job_fresh-start-journey/artifacts/9ngauisu_45340319.png"
                  alt="LegitScript Certified"
                  className="w-14 h-14 object-contain shrink-0"
                  loading="lazy"
                  draggable={false}
                />
                <p className="font-serif text-forest text-base leading-tight font-bold">LegitScript<br /><span className="text-ink2 text-[11px] font-medium tracking-normal">Certified</span></p>
              </a>
            </div>

            <p className="mt-6 text-xs text-terracotta uppercase tracking-[0.2em] font-semibold">Immediate admittance · Shorter wait lists</p>
          </div>
        </div>

        <div className="mt-10 pt-8 border-t border-sand/10">
          {/* Shaun's founding quote */}
          <blockquote className="max-w-3xl mb-8" data-testid="footer-founder-quote">
            <p className="font-serif italic text-sand/85 text-base md:text-lg leading-relaxed">
              &ldquo;Recovery has taught me that the darkest nights can lead to the brightest days.
              I am proof that hope and healing are real.&rdquo;
            </p>
            <p className="mt-2 text-terracotta uppercase tracking-[0.22em] text-[10px] font-semibold">— Shaun S. · Member of Recovery</p>
          </blockquote>
          <div className="flex flex-col md:flex-row justify-between gap-4 text-sm text-sand/60">
            <p>© {new Date().getFullYear()} New Beginnings Recovery Home. All rights reserved.</p>
            <div className="flex flex-wrap gap-x-5 gap-y-2">
              <Link to="/privacy" className="hover:text-terracotta transition-colors">Privacy</Link>
              <Link to="/terms" className="hover:text-terracotta transition-colors">Terms</Link>
              <a href={CONTACT.phoneHref} className="hover:text-terracotta transition-colors">Help &amp; Contact</a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
